import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SearchBox from '@/components/base/SearchBox';
import CategoryCard from '@/components/base/CategoryCard';
import SiteCard from '@/components/base/SiteCard';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { sites } from '@/mocks/sites';
import { articles } from '@/mocks/articles';

export default function Home() {
  const hotSites = sites.filter((s) => s.isHot).slice(0, 8);
  const newSites = sites.filter((s) => s.isNew);
  const topSites = [...sites].sort((a, b) => a.rank - b.rank).slice(0, 12);

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '九色导航',
    url: 'https://www.jiusenavigation.com',
    description: '专业的一站式网址导航平台，汇聚海量优质网站资源，涵盖娱乐、购物、新闻、工具、学习等多个分类。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.baidu.com/s?wd={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '九色导航 - 一站式网址导航平台',
    description: '九色导航是专业的一站式网址导航平台，汇聚海量优质网站资源。让上网更简单、更高效。',
    url: 'https://www.jiusenavigation.com',
    isPartOf: {
      '@type': 'WebSite',
      name: '九色导航',
      url: 'https://www.jiusenavigation.com',
    },
  };

  return (
    <>
      <SEOHead
        title="九色导航 - 一站式网址导航平台 | 网址导航大全"
        description="九色导航是专业的一站式网址导航平台，汇聚海量优质网站资源，涵盖娱乐、购物、新闻、工具、学习等多个分类。九色导航让上网更简单、更高效，是您每天必备的浏览器导航首页。"
        keywords="九色导航,九色导航网址大全,九色导航官网,网址导航,导航网站推荐,浏览器导航首页,优质网站导航,网站收录导航"
        canonicalUrl="/"
        schemaJson={{ ...webSiteSchema, ...webPageSchema }}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20warm%20orange%20and%20cream%20gradient%20background%2C%20soft%20flowing%20shapes%20in%20light%20pastel%20tones%2C%20minimal%20modern%20design%20with%20smooth%20curves%2C%20clean%20aesthetic%20digital%20art%20style%20with%20warm%20ambient%20light&width=1600&height=900&seq=jiuse-hero-bg-01&orientation=landscape"
              alt="九色导航背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-background-50"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-4">
                九色导航
              </h1>
              <p className="text-sm md:text-base text-background-100/90 max-w-xl mx-auto leading-relaxed">
                专业的一站式网址导航平台，汇聚海量优质网站资源<br className="hidden sm:block" />
                让上网更简单、更高效，您每天必备的浏览器导航首页
              </p>
            </div>
            <SearchBox />
            <div className="mt-6 flex items-center justify-center gap-4 flex-wrap">
              <span className="text-xs text-background-100/60">热门搜索：</span>
              {['B站', '淘宝', '京东', '百度新闻', '抖音', '知乎'].map((tag) => (
                <a
                  key={tag}
                  href={`https://www.baidu.com/s?wd=${encodeURIComponent(tag)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-xs px-3 py-1.5 rounded-full bg-background-50/15 text-background-100 hover:bg-background-50/25 transition-colors backdrop-blur-sm whitespace-nowrap cursor-pointer"
                >
                  {tag}
                </a>
              ))}
            </div>
          </div>
        </header>

        <main>
          {/* Categories Section */}
          <section className="py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  九大分类，一站直达
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                  精心划分九大核心分类，覆盖您上网需求的方方面面。点击任意分类，探索更多优质网站。
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((cat) => {
                  const count = sites.filter((s) => s.categorySlug === cat.slug).length;
                  return (
                    <CategoryCard
                      key={cat.id}
                      slug={cat.slug}
                      name={cat.name}
                      icon={cat.icon}
                      description={cat.description}
                      color={cat.color}
                      siteCount={count}
                    />
                  );
                })}
              </div>
            </div>
          </section>

          {/* Hot Sites Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/50">
            <div className="max-w-screen-2xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                    热门推荐
                  </h2>
                  <p className="text-sm text-foreground-500">精选当下最受欢迎的优质网站</p>
                </div>
                <Link
                  to="/articles"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  更多推荐 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {hotSites.map((site) => (
                  <SiteCard key={site.id} site={site} />
                ))}
              </div>
            </div>
          </section>

          {/* Ranking & Latest Section */}
          <section className="py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-screen-2xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Top Ranking */}
                <div className="lg:col-span-2">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                      排行榜
                    </h2>
                    <Link
                      to="/articles"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      查看全部 <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                  <div className="bg-background-50 rounded-2xl border border-background-200/70 divide-y divide-background-100">
                    {topSites.map((site, idx) => (
                      <a
                        key={site.id}
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center gap-4 px-5 py-3.5 hover:bg-background-100/50 transition-colors cursor-pointer group"
                      >
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          idx < 3
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-500'
                        }`}>
                          {idx + 1}
                        </span>
                        <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                          <span className="text-xs font-semibold text-primary-600">{site.name.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                            {site.name}
                          </h3>
                          <p className="text-xs text-foreground-500 line-clamp-1">{site.description}</p>
                        </div>
                        <div className="flex-shrink-0 text-xs text-foreground-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          访问 <i className="ri-arrow-right-up-line"></i>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Latest Articles */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                      最新文章
                    </h2>
                    <Link
                      to="/articles"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      全部 <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {articles.slice(0, 4).map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="flex gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
                      >
                        <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {article.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">
                            {article.publishDate} · 阅读{article.readTime}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100/50">
            <div className="max-w-screen-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4">
                开始使用九色导航
              </h2>
              <p className="text-sm text-foreground-500 max-w-md mx-auto mb-8">
                将九色导航设置为您的浏览器首页，每天打开浏览器即可快速访问所有常用网站。
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/articles"
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览文章指南
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-3 bg-background-50 text-foreground-800 rounded-xl text-sm font-medium border border-background-300 hover:border-foreground-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}